import { useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import Typography from '@mui/material/Typography'

import CSVIO from 'components/CSVIO'
import ErrorDialog from 'components/ErrorDialog'
import { Flex, FlexContainer } from 'components/Flex'
import ListAction from 'components/ListAction'
import ListFilter from 'components/ListFilter'
import ListItem from 'components/ListItem'
import ListSort from 'components/ListSort'
import Panel from 'components/Panel'
import { doc, orderBy, useDocs, where, writeBatch } from 'lib/firestore'
import { categories } from 'utils/constant'

import ProductDialog from './ProductDialog'

export default function ProductList({ count }) {
  const [data, setData] = useState(null)
  const [sort, setSort] = useState({ field: 'name', order: 'asc' })
  const [filter, setFilter] = useState({ category: '*' })
  const [error, setError] = useState(null)
  const conditions = useMemo(() => {
    const _conditions = []
    Object.entries(filter).forEach(([field, value]) => {
      if (value !== '*') {
        _conditions.push(where(field, '==', value))
      }
    })
    _conditions.push(orderBy(sort.field, sort.order))
    return _conditions
  }, [filter, sort])
  const { items } = useDocs('products', ...conditions)

  if (!items) {
    return (
      <FlexContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </FlexContainer>
    )
  }

  const handleChangeFilter = (field, value) => {
    setFilter(current => ({ ...current, [field]: value }))
  }

  const handleChangeSort = field => {
    setSort(current => ({
      field,
      order: current.field !== field || current.order === 'desc' ? 'asc' : 'desc',
    }))
  }

  const saveProducts = async values => {
    const batch = writeBatch()
    values.forEach(value => {
      const docRef = doc(`products/${value.code}`)
      batch.set(docRef, value)
    })
    await batch.commit()
  }

  return (
    <Flex>
      <ListAction>
        <ListFilter
          value={filter}
          fields={[{ id: 'category', name: 'カテゴリ', options: categories }]}
          onChange={handleChangeFilter}
        />
        <Box sx={{ width: 16 }} />
        <CSVIO
          onChange={saveProducts}
          onError={error => setError(error.message)}
          format={{
            code: { name: 'JAN コード（必須）', required: 'JAN コードは必須項目です' },
            name: { name: '商品名（必須）', required: '商品名は必須項目です' },
            category: {
              name: 'カテゴリー',
              convert: v => (categories.includes(v) ? v : 'その他'),
            },
            standard: { name: '規格' },
            price: { name: '単価', convert: v => parseInt(v) || 0 },
          }}
          data={items}
        />
      </ListAction>
      <Panel>
        <ListSort
          title={`商品一覧（全 ${count} 件）`}
          fields={[
            { id: 'name', name: '商品名' },
            { id: 'price', name: '金額' },
          ]}
          value={sort}
          onChange={handleChangeSort}
        />
        {items.map(x => (
          <ListItem
            key={x.id}
            primary={x.name}
            secondary={`${x.category}・${x.standard}・${x.price} 円`}
            onClick={() => setData(x)}
          />
        ))}
        {items.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography style={{ fontSize: 14, fontWeight: 'bold' }} variant="body2">
              商品は登録されていません
            </Typography>
            <Button color="primary" onClick={() => setData({})}>
              商品を登録する
            </Button>
          </Box>
        )}
      </Panel>
      <Fab
        color="primary"
        style={{ position: 'fixed', right: 16, bottom: 16 }}
        onClick={() => setData({})}
      >
        <AddIcon />
      </Fab>
      {data && <ProductDialog data={data} onClose={() => setData(null)} />}
      {Boolean(error) && <ErrorDialog error={error} onClose={() => setError(null)} />}
    </Flex>
  )
}
